import * as msal from '@azure/msal-browser';
import { App } from 'vue';
import { useDosingStore } from '@/stores/dosing';
import { Member } from '@/models/Member';

export interface MsalPluginOptions {
  clientId: string;
  loginAuthority: string;
  passwordAuthority: string;
  knownAuthority: string;
  scopes: string;
  loginUri: string;
  logoutUri: string;
}

export interface IMsalPlugin {
  isAuthenticated: boolean;
  install(app: App, options?: MsalPluginOptions): Promise<void>;
  login(): Promise<any>;
  logout(): Promise<void>;
  getUser(): any;
}

let msalInstance: msal.IPublicClientApplication;

const B2C_FORGOT_PASSWORD = 'AADB2C90118';
const B2C_USER_CANCELLED = 'AADB2C90091';

function hasB2CErrorCode(e: any, code: string): boolean {
  return [e?.errorCode, e?.errorMessage, e?.message].some(
    (s) => typeof s === 'string' && s.includes(code),
  );
}

export function accountToMember(account: msal.AccountInfo): Member {
  const claims = (account.idTokenClaims ?? {}) as Record<string, any>;
  return new Member({
    name: claims['given_name'] ?? account.name ?? 'Member',
    email: account.username || claims['emails']?.[0] || '',
    oid: account.homeAccountId ?? '',
  });
}

export let msalPluginInstance: IMsalPlugin;

export class MsalPlugin implements IMsalPlugin {
  private pluginOptions: MsalPluginOptions = {
    clientId: '',
    loginAuthority: '',
    passwordAuthority: '',
    knownAuthority: '',
    scopes: '',
    loginUri: '',
    logoutUri: '',
  };

  public isAuthenticated = false;

  public async install(app: App, options?: MsalPluginOptions): Promise<void> {
    if (!options) {
      throw new Error('MsalPluginOptions must be specified');
    }
    this.pluginOptions = options;
    await this.initialize(options);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    msalPluginInstance = this;
    app.config.globalProperties.$msal = this;

    // Process the response when returning from B2C via loginRedirect
    const result = await this.handleRedirect();
    if (result?.account) {
      msalInstance.setActiveAccount(result.account);
      this.isAuthenticated = true;
      useDosingStore().login(accountToMember(result.account));
      return;
    }

    // Restore auth state from MSAL token cache (handles page refresh with active session)
    if (this.isAuthenticated) {
      const user = this.getUser();
      if (user) {
        useDosingStore().login(accountToMember(user));
      }
    }
  }

  private async handleRedirect(): Promise<msal.AuthenticationResult | null> {
    try {
      return await msalInstance.handleRedirectPromise();
    } catch (e: any) {
      if (hasB2CErrorCode(e, B2C_FORGOT_PASSWORD)) {
        // User clicked "Forgot your password?" in the sign-in flow
        await msalInstance.loginRedirect({
          authority: this.pluginOptions.passwordAuthority,
          scopes: ['openid'],
        });
      } else if (hasB2CErrorCode(e, B2C_USER_CANCELLED)) {
        console.debug('B2C interaction cancelled by user');
      } else {
        console.error(e);
      }
      return null;
    }
  }

  private async initialize(options: MsalPluginOptions): Promise<void> {
    const msalConfig: msal.Configuration = {
      auth: {
        clientId: options.clientId,
        authority: options.loginAuthority,
        knownAuthorities: [options.knownAuthority],
        redirectUri: options.loginUri,
        postLogoutRedirectUri: options.logoutUri,
      },
      system: {
        loggerOptions: {
          loggerCallback: (level: msal.LogLevel, message: string, containsPii: boolean): void => {
            if (containsPii) {
              return;
            }
            switch (level) {
              case msal.LogLevel.Error:
                console.error(message);
                return;
              case msal.LogLevel.Info:
                console.info(message);
                return;
              case msal.LogLevel.Verbose:
                console.debug(message);
                return;
              case msal.LogLevel.Warning:
                console.warn(message);
                return;
            }
          },
          piiLoggingEnabled: false,
          logLevel: msal.LogLevel.Verbose,
        },
      },
    };
    msalInstance = new msal.PublicClientApplication(msalConfig);
    await msalInstance.initialize();
    this.isAuthenticated = this.getIsAuthenticated();
  }

  /**
   * Returns the cached account if signed in; otherwise navigates away to B2C.
   * The response is processed by handleRedirectPromise in install() on return.
   */
  public async login() {
    const account = this.getUser();
    if (account === null) {
      const loginRequest: msal.RedirectRequest = {
        scopes: ['openid'],
        prompt: 'login',
      };
      await msalInstance.loginRedirect(loginRequest);
    }
    return account;
  }

  public async logout() {
    const logoutRequest = {
      postLogoutRedirectUri: this.pluginOptions.logoutUri,
      mainWindowRedirectUri: this.pluginOptions.logoutUri,
    };
    await msalInstance.clearCache();
    await msalInstance.handleRedirectPromise();
    await msalInstance.logoutRedirect(logoutRequest);
    this.isAuthenticated = false;
  }

  private getIsAuthenticated(): boolean {
    const accounts: msal.AccountInfo[] = msalInstance.getAllAccounts();
    return accounts && accounts.length > 0;
  }

  public getUser() {
    const userAccounts: msal.AccountInfo[] = msalInstance.getAllAccounts();
    if (userAccounts.length <= 0) {
      return null;
    } else {
      msalInstance.setActiveAccount(userAccounts[0] ?? null);
      return msalInstance.getActiveAccount();
    }
  }
}

/**
 * Dev-only plugin that bypasses Azure B2C authentication.
 * Activated when VITE_AUTH_DISABLED=true in the environment (e.g. .env.local).
 * Never set VITE_AUTH_DISABLED in production builds.
 */
export class DevMsalPlugin implements IMsalPlugin {
  public isAuthenticated = true;

  private readonly mockUser = {
    idTokenClaims: { given_name: 'Dev User' },
    username: 'dev@local',
    homeAccountId: 'dev-local',
    idToken: 'dev-mock-token',
    name: 'Dev User',
  };

  public async install(app: App, _options?: MsalPluginOptions): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    msalPluginInstance = this;
    app.config.globalProperties.$msal = this;
    console.warn('[DevMsalPlugin] Auth is DISABLED — using mock user. Do not use in production.');
    const store = useDosingStore();
    store.login(this.mockUser);
  }

  public async login(): Promise<any> {
    return this.mockUser;
  }

  public async logout(): Promise<void> {
    // no-op in dev mode
  }

  public getUser(): any {
    return this.mockUser;
  }
}
